'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslations } from 'next-intl';

import {
  PRIMARY_DEVICE,
  type CanvasFrameRefs,
} from '@/components/CanvasPage/canvasConstants';
import { useCommentsContext } from '@/components/Contexts/CommentsContext';
import {
  hideNavigationToast,
  showNavigationToast,
} from '@/components/ui/ToastNotification';
import {
  buildPreviewFrameUrl,
  buildPreviewSiteUrl,
} from '@/lib/previewFrameUrl';
import { EditorType } from '@/types/feedback';

import type {
  WorkbenchMode,
  WorkbenchPinPopoverModel,
  WorkbenchView,
} from './types';
import type { ProxyFrameHandle } from '@/components/ProxyFrame/ProxyFrame';
import type { DevicePreset, Pin } from '@/types/feedback';

export type {
  WorkbenchMode,
  WorkbenchPinPopoverModel,
  WorkbenchView,
} from './types';

const isDev = process.env.NODE_ENV === 'development';
const isOpenPin = (pin: Pin) => pin.status !== 'RESOLVED';

// The workbench has a single frame that serves every device, while the canvas
// mounts one frame per device, so frames are tracked by what they render.
type FrameKey = 'workbench' | DevicePreset;

export function useWorkbenchPageState(
  targetUrl: string,
  isPartner = false,
  editorType?: EditorType,
) {
  const t = useTranslations();

  const {
    state: { pins, visitorId },
    dispatch,
  } = useCommentsContext();

  const [device, setDeviceState] = useState<DevicePreset>('desktop');
  const [classicEditorFrameUrl, setClassicEditorFrameUrl] = useState(targetUrl);
  const [mode, setMode] = useState<WorkbenchMode>('comment');
  const [view, setView] = useState<WorkbenchView>('workbench');
  const [sidebarOpen, setSidebarOpen] = useState(
    () => isPartner && pins.length > 0,
  );
  // Auto-open the sidebar for partners once comments are available. The lazy
  // initializer above only covers the case where pins are pre-loaded via
  // initialData; when they arrive on the async load path this effect opens the
  // sidebar on the first 0 -> >0 transition. It fires once, so an explicit
  // user-close is preserved.
  const didAutoOpenSidebarRef = useRef(false);
  useEffect(() => {
    if (didAutoOpenSidebarRef.current) return;
    if (isPartner && pins.length > 0) {
      didAutoOpenSidebarRef.current = true;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- open once when async-loaded comments arrive
      setSidebarOpen(true);
    }
  }, [isPartner, pins.length]);
  const [fullscreen, setFullscreen] = useState(false);
  const [currentPagePath, setCurrentPagePath] = useState<string | null>(null);
  const currentPagePathRef = useRef<string | null>(null);
  const [activePin, setActivePin] = useState<{
    id: string;
    x: number;
    y: number;
  } | null>(null);

  const proxyFrameRef = useRef<ProxyFrameHandle>(null);
  const canvasFrameRefs = useRef<CanvasFrameRefs>({});
  const pendingScrollRef = useRef<{
    pinId: string;
    device: DevicePreset;
  } | null>(null);
  const frameReadyRef = useRef<Partial<Record<FrameKey, boolean>>>({});
  // True between asking the frame to navigate and it reporting ready again.
  const pendingNavigationRef = useRef(false);
  const pinMode = mode === 'comment';
  const pinsVisible = true;
  const isCanvas = view === 'canvas';

  // A pin only exists in the frame that renders its device, so every message
  // addressed to a pin has to go to that frame rather than a single default.
  const getFrame = useCallback(
    (frameDevice: DevicePreset): ProxyFrameHandle | null =>
      isCanvas
        ? (canvasFrameRefs.current[frameDevice] ?? null)
        : proxyFrameRef.current,
    [isCanvas],
  );

  const isFrameReady = useCallback(
    (frameDevice: DevicePreset) =>
      frameReadyRef.current[isCanvas ? frameDevice : 'workbench'] === true,
    [isCanvas],
  );

  const handlePinAdded = useCallback(
    (
      partial: Omit<Pin, 'label' | 'createdAt'>,
      viewportX: number,
      viewportY: number,
    ) => {
      const newPin: Pin = {
        ...partial,
        commenterId: partial.commenterId ?? visitorId ?? undefined,
        label: '',
        createdAt: Date.now(),
        devicePreset: partial.devicePreset ?? device,
      };
      dispatch({ type: 'ADD_PIN', payload: newPin });
      setActivePin({ id: newPin.id, x: viewportX, y: viewportY });
    },
    [device, dispatch, visitorId],
  );

  const handlePinMarkerClick = useCallback(
    (pinId: string, x: number, y: number) => {
      setActivePin((prev) => (prev?.id === pinId ? null : { id: pinId, x, y }));
    },
    [],
  );

  const handleDeletePin = useCallback(
    (id: string) => {
      dispatch({ type: 'DELETE_PIN', payload: { id } });
    },
    [dispatch],
  );

  const handleUpdatePinLabel = useCallback(
    (id: string, label: string) => {
      dispatch({ type: 'UPDATE_PIN_LABEL', payload: { id, label } });
    },
    [dispatch],
  );

  const handleScrolledToPin = useCallback(
    (pinId: string, viewportX: number, viewportY: number) => {
      hideNavigationToast();
      setActivePin({ id: pinId, x: viewportX, y: viewportY });
    },
    [],
  );

  const handlePageChanged = useCallback((pagePath: string) => {
    currentPagePathRef.current = pagePath;
    setCurrentPagePath(pagePath);
  }, []);

  const handleCommentPersisted = useCallback(
    (pinId: string, commentId: string, revision: string | null) => {
      dispatch({
        type: 'COMMENT_PERSISTED',
        payload: { pinId, commentId, revision },
      });
    },
    [dispatch],
  );

  const setDevice = useCallback(
    (preset: DevicePreset) => {
      setDeviceState(preset);
      if (editorType === EditorType.Editor) {
        const pagePath = currentPagePathRef.current;
        setClassicEditorFrameUrl(
          pagePath ? new URL('/' + pagePath, targetUrl).href : targetUrl,
        );
      }
    },
    [editorType, targetUrl],
  );

  const handleSidebarPinClick = useCallback(
    (pinId: string) => {
      const pin = pins.find((p) => p.id === pinId);
      if (!pin || !isOpenPin(pin)) return;
      if (!pin.isLegacy && (pin.offsetXPct == null || pin.offsetYPct == null))
        return;

      // Only one comment can be navigated to at a time. A slow or failed
      // navigation would otherwise keep its pending scroll and its banner
      // alive, so this click supersedes it instead of stacking on top.
      const supersededNavigation = pendingNavigationRef.current;
      if (pendingScrollRef.current) {
        pendingScrollRef.current = null;
        hideNavigationToast();
      }

      // The sidebar lists pins for every device (allDevices), so a click may
      // target a pin that isn't rendered in the current workbench device.
      // Align the device with the pin first, then defer the scroll until the
      // pin becomes visible — otherwise navigation feedback hangs because an
      // off-device pin never appears in visiblePins (see flushPendingScroll).
      // The canvas shows every device at once, so its pins are already
      // rendered and switching the workbench device would only move the frame
      // the click is never sent to.
      const targetDevice = pin.devicePreset ?? device;
      const deviceChanged = !isCanvas && targetDevice !== device;
      if (deviceChanged) setDevice(targetDevice);

      const pinPage = pin.pagePath ?? '';
      const currentPage = currentPagePath ?? '';
      const samePage = !pinPage || pinPage === currentPage;

      // A navigation already handed to the bridge cannot be recalled — it has
      // assigned window.location, so the frame will still land on the old page.
      // Dropping the deferred scroll is therefore not enough: this click has to
      // navigate to the page it wants, even when that is the page shown now.
      const navigating = !samePage || supersededNavigation;

      // Already on the right page and device, with nothing in flight: scroll
      // now only if the target bridge is ready. Canvas frames load
      // independently, so an early postMessage would otherwise be dropped.
      if (!navigating && !deviceChanged && isFrameReady(targetDevice)) {
        getFrame(targetDevice)?.scrollToPin(pinId);
        return;
      }

      if (navigating) {
        const path = '/' + (samePage ? currentPage : pinPage);
        // Guard against open-redirect: a crafted pagePath must not resolve to a
        // different origin than the site we are proxying. Reject anything that
        // escapes targetUrl's origin before it reaches FB_NAVIGATE.
        try {
          if (new URL(path, targetUrl).origin !== new URL(targetUrl).origin) {
            return;
          }
        } catch {
          return;
        }
        // Only the frame receiving FB_NAVIGATE is known to be loading now.
        // Other canvas frames keep their readiness until their targetUrl
        // actually changes and their own onLoading callback fires.
        const navigationDevice = isCanvas ? PRIMARY_DEVICE : targetDevice;
        const navigationFrameKey: FrameKey = isCanvas
          ? navigationDevice
          : 'workbench';
        frameReadyRef.current[navigationFrameKey] = false;
        pendingNavigationRef.current = true;
        showNavigationToast(t('get-feedback-toast-navigating'));
        const pageUrl = new URL(path, targetUrl).href;
        // Canvas frames all follow the page the primary frame reports, so the
        // navigation is addressed to it and the others reload behind it.
        const navigateUrl = isDev
          ? buildPreviewFrameUrl(pageUrl, {
              editorType,
              device: navigationDevice,
            })
          : buildPreviewSiteUrl(path, { editorType, device: navigationDevice });
        getFrame(navigationDevice)?.sendMessage({
          type: 'FB_NAVIGATE',
          payload: { url: navigateUrl },
        });
      }

      // Defer the scroll: flushPendingScroll fires once the pin re-renders in
      // the aligned device view (and, for cross-page clicks, after the frame
      // reloads and reports ready).
      pendingScrollRef.current = { pinId, device: targetDevice };
    },
    [
      pins,
      currentPagePath,
      targetUrl,
      t,
      editorType,
      device,
      setDevice,
      isCanvas,
      getFrame,
      isFrameReady,
    ],
  );

  const visiblePins = useMemo(
    () =>
      pins.filter(
        (p) =>
          isOpenPin(p) &&
          (!p.devicePreset || p.devicePreset === device) &&
          (!p.pagePath || !currentPagePath || p.pagePath === currentPagePath),
      ),
    [pins, device, currentPagePath],
  );

  const pageFilteredPins = useMemo(
    () =>
      pins.filter(
        (p) =>
          isOpenPin(p) &&
          (!p.pagePath || !currentPagePath || p.pagePath === currentPagePath),
      ),
    [pins, currentPagePath],
  );

  const canvasTargetUrl = useMemo(() => {
    if (!currentPagePath) return targetUrl;
    const path = '/' + currentPagePath;
    return new URL(path, targetUrl).href;
  }, [currentPagePath, targetUrl]);

  const workbenchFrameUrl =
    editorType === EditorType.Editor ? classicEditorFrameUrl : targetUrl;

  const flushPendingScroll = useCallback(() => {
    const pending = pendingScrollRef.current;
    if (!pending) return;
    if (!isFrameReady(pending.device)) return;
    // Canvas frames each render their own device, so a pin is addressable as
    // soon as it belongs to the current page.
    const renderedPins = isCanvas ? pageFilteredPins : visiblePins;
    if (!renderedPins.some((p) => p.id === pending.pinId)) return;
    pendingScrollRef.current = null;
    getFrame(pending.device)?.scrollToPin(pending.pinId);
  }, [visiblePins, pageFilteredPins, isCanvas, isFrameReady, getFrame]);

  const handleFrameLoading = useCallback((frameDevice?: DevicePreset) => {
    frameReadyRef.current[frameDevice ?? 'workbench'] = false;
  }, []);

  const handleFrameReady = useCallback(
    (frameDevice?: DevicePreset) => {
      frameReadyRef.current[frameDevice ?? 'workbench'] = true;
      // Canvas navigation is controlled by the primary frame. A secondary
      // frame becoming ready must not make an in-flight navigation look done.
      if (!isCanvas || frameDevice === PRIMARY_DEVICE) {
        pendingNavigationRef.current = false;
      }
      flushPendingScroll();
    },
    [flushPendingScroll, isCanvas],
  );

  useEffect(() => {
    flushPendingScroll();
  }, [flushPendingScroll]);

  const pinPopoverModel = useMemo((): WorkbenchPinPopoverModel | null => {
    if (!activePin) return null;
    const pin = pins.find((p) => p.id === activePin.id);
    if (!pin || !isOpenPin(pin)) return null;
    return {
      pin,
      pinIndex: pins.indexOf(pin) + 1,
      anchorX: activePin.x,
      anchorY: activePin.y,
    };
  }, [activePin, pins]);

  return {
    targetUrl,
    workbenchFrameUrl,
    canvasTargetUrl,
    device,
    setDevice,
    visiblePins,
    pageFilteredPins,
    mode,
    setMode,
    view,
    setView,
    pins,
    currentPagePath,
    sidebarOpen,
    setSidebarOpen,
    fullscreen,
    setFullscreen,
    activePin,
    setActivePin,
    proxyFrameRef,
    canvasFrameRefs,
    handleFrameLoading,
    handleFrameReady,
    pinMode,
    pinsVisible,
    handlePinAdded,
    handlePinMarkerClick,
    handleScrolledToPin,
    handleDeletePin,
    handleUpdatePinLabel,
    handleCommentPersisted,
    handlePageChanged,
    handleSidebarPinClick,
    pinPopoverModel,
  };
}
